import { useCallback, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../api.js';
import { toast } from './Toasts.jsx';
import { focusHref } from './ScheduledList.jsx';

// T447 — follow-ups he has written, waiting for him to press send.
//
// 27 applications have been silent for 14 or more days and not one
// follow-up has ever been sent, though the product has been able to compose
// them since T325. It could never send one: `markSent` wrote a date and
// nothing left the building.
//
// Every property here is a refusal to be clever with his name:
//  - one button per message, no "send all", because fourteen emails to real
//    employers should cost fourteen decisions;
//  - the text is shown in full, editable, and what is sent is what is on
//    screen — the endpoint takes the subject and body back and will not
//    send a draft he has not read;
//  - the recipient is the address the employer actually wrote from, never a
//    guessed `careers@`. With no correspondence, he types it.

const FollowUpsToSend = forwardRef(function FollowUpsToSend({ onCount }, ref) {
  const [rows, setRows] = useState(null);
  // T434 — invisible when there is nothing is right; invisible when the
  // load could not run says "nothing" and means "I do not know".
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(null);
  const [draft, setDraft] = useState({ subject: '', body: '', to: '' });
  const [busy, setBusy] = useState(false);
  const [writing, setWriting] = useState(null);

  // T457 — write the one that has not been written.
  //
  // The section listed only rows that already had a draft, and nothing
  // composes them in bulk — so with his two drafts sitting on rejected
  // applications it rendered empty while 26 live ones had been silent for
  // a fortnight or more. One button per row, because each is an AI call
  // and fourteen of them should cost fourteen decisions.
  const write = async (r) => {
    setWriting(r.id);
    try {
      const res = await api.post(`/recommendations/${r.id}/follow-up-email`, {});
      toast('Draft written — read it before you send.', { kind: 'success' });
      await load();
      const fresh = res?.followUpEmail ?? {};
      setOpen(r.id);
      setDraft({ subject: fresh.subject ?? '', body: fresh.body ?? '', to: r.suggestedTo ?? '' });
    } catch (e) {
      toast(e.body?.message || 'Could not write that one.', { kind: 'error' });
    } finally {
      setWriting(null);
    }
  };

  const load = useCallback(async () => {
    try {
      const r = await api.get('/recommendations/follow-ups-pending');
      setRows(r.items ?? []);
      setFailed(false);
      onCount?.(r.items?.length ?? 0);
    } catch {
      setFailed(true);
      setRows([]);
      onCount?.(0);
    }
  }, [onCount]);

  useImperativeHandle(ref, () => ({ reload: load }), [load]);
  useEffect(() => {
    load();
  }, [load]);

  const expand = (r) => {
    setOpen(r.id);
    setDraft({ subject: r.subject ?? '', body: r.body ?? '', to: r.suggestedTo ?? '' });
  };

  const send = async (r) => {
    setBusy(true);
    try {
      const res = await api.post(`/recommendations/${r.id}/follow-up-email/send`, {
        subject: draft.subject,
        body: draft.body,
        ...(draft.to ? { to: draft.to } : {}),
      });
      toast(`Sent to ${res.to}.`, { kind: 'success' });
      setOpen(null);
      await load();
    } catch (e) {
      toast(e.body?.message || e.body?.error || 'Could not send.', { kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  if (failed) {
    return (
      <section className="space-y-2">
        <h2 className="text-sm font-medium text-brand-500">Follow-ups ready to send</h2>
        <p className="text-xs text-slate-500">
          Could not load your drafts just now — this is not the same as having none.
        </p>
      </section>
    );
  }
  if (!rows || rows.length === 0) return null;

  return (
    <section className="space-y-2">
      <div className="flex items-baseline gap-2">
        <h2 className="text-sm font-medium text-brand-500">Follow-ups ready to send</h2>
        <span className="text-[11px] text-slate-500">{rows.length}</span>
      </div>
      <p className="max-w-prose text-xs text-slate-500">
        Applications that have gone quiet for a fortnight or more. Nothing is written until you ask
        for it, and nothing is sent until you read it — each goes out on its own button, from your
        own address, with the text on screen.
      </p>
      <ul className="space-y-1.5">
        {rows.map((r) => (
          <li key={r.id} className="rounded-md border border-slate-800 bg-slate-950/40 px-3 py-2">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="text-sm text-slate-200">{r.company}</span>
              <span className="min-w-0 flex-1 truncate text-xs text-slate-500">{r.title}</span>
              <span className="font-mono text-[11px] text-slate-500">{r.silentDays}d silent</span>
              {/* T457 — say which letter this would be. A nudge after a
                  conversation is not the same message as one after
                  silence, and he should know which before spending a call
                  on it. */}
              {r.situation === 'after-meeting' && (
                <span className="rounded border border-brand-700 px-1.5 py-0.5 text-[10px] text-brand-400">
                  {/* T458 — how long ago, when an invitation records it.
                      `metDaysAgo` is null rather than 0 when nothing does,
                      because "met 0 days ago" would be a claim we cannot
                      make (T387: empty is not the same as failed). */}
                  {r.metDaysAgo == null
                    ? 'after your interview'
                    : `met ${r.metDaysAgo} day${r.metDaysAgo === 1 ? '' : 's'} ago`}
                </span>
              )}
              {/* T472 — the language the letter will be in. The one he sent to
                  JP/Politiken came out Danish for an English advert because
                  nothing chose and the model did; now it is visible before
                  the call, not discovered in the draft. */}
              {r.draftLanguage && r.draftLanguage !== 'en' && (
                <span className="rounded border border-slate-700 px-1.5 py-0.5 text-[10px] uppercase text-slate-400">
                  {r.draftLanguage}
                </span>
              )}
              {r.situation === 'in-process' && (
                <span className="rounded border border-slate-700 px-1.5 py-0.5 text-[10px] text-slate-400">
                  meeting booked
                </span>
              )}
            </div>

            {open === r.id ? (
              <div className="mt-2 space-y-1.5">
                <input
                  value={draft.to}
                  onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
                  placeholder={r.suggestedTo || 'nobody has written — type an address'}
                  className="w-full rounded-md border border-slate-700 bg-slate-950/40 px-2 py-1 text-xs text-slate-100"
                  aria-label="Recipient"
                />
                <input
                  value={draft.subject}
                  onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))}
                  className="w-full rounded-md border border-slate-700 bg-slate-950/40 px-2 py-1 text-xs text-slate-100"
                  aria-label="Subject"
                />
                <textarea
                  value={draft.body}
                  onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
                  rows={8}
                  className="w-full rounded-md border border-slate-700 bg-slate-950/40 px-2 py-1 text-xs text-slate-100"
                  aria-label="Message"
                />
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    disabled={busy || !draft.subject.trim() || !draft.body.trim()}
                    onClick={() => send(r)}
                    className="rounded-md border border-brand-600 bg-brand-600/10 px-3 py-1 text-xs text-brand-300 transition hover:bg-brand-600/10 disabled:opacity-50"
                  >
                    {busy ? 'sending…' : 'Send this one'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setOpen(null)}
                    className="text-[11px] text-slate-500 underline decoration-dotted hover:text-slate-300"
                  >
                    Not now
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
                {r.hasDraft ? (
                  <button
                    type="button"
                    onClick={() => expand(r)}
                    className="text-slate-300 underline decoration-dotted transition hover:text-brand-400"
                  >
                    Read it and send
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={writing === r.id}
                    onClick={() => write(r)}
                    className="text-slate-300 underline decoration-dotted transition hover:text-brand-400 disabled:opacity-50"
                    title="Writes a draft with Claude — about $0.01. Nothing is sent."
                  >
                    {writing === r.id ? 'writing…' : 'Write one'}
                  </button>
                )}
                <Link
                  to={focusHref(r.id)}
                  className="text-slate-500 underline decoration-dotted transition hover:text-slate-300"
                >
                  Open the application
                </Link>
                {!r.suggestedTo && <span className="text-amber-400">no address on file</span>}
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
});

export default FollowUpsToSend;
